import { Box, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../../../../hooks/useApi';
import { useApiData } from '../../../../hooks/useApiData';
import { useUser } from '../../../../hooks/useUser';
import { getCompanyConfigValue } from '../../../../utils/companyConfig';
import OpenEnrollmentNotification from '../../../benefits/OpenEnrollmentNotification';
import CsaAcknowledgementPrompt from '../../../dashboard/CsaAcknowledgementPrompt';
import HandbookAssignmentPrompt from '../../../dashboard/HandbookAssignmentPrompt';
import UpdateUserDefinedFieldsModal from '../../../profile/UpdateUserDefinedFieldsModal';
import AppNotification from '../Notifications/AppNotification';
import ClientDropdown from './ClientDropdown';
import HeaderSearch from './HeaderSearch';
import MobileSection from './MobileSection';
import Profile from './Profile';

const HeaderContent: React.FC = () => {
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const [customFieldsModalOpen, setCustomFieldsModalOpen] =
    useState<boolean>(false);

  const { user, company } = useUser();

  const csaAcknowledgementEnabled =
    !!company &&
    getCompanyConfigValue(company, 'csa_acknowledgement_enabled', false);

  const { data: customFields, refresh: refreshCustomFields } = useApiData(
    (api) => api.profiles.getEmployeeCustomFields()
  );

  const api = useApi();
  const [employeeProfile, setEmployeeProfile] =
    useState<EmployeeProfile | null>(null);

  const hasRequiredCustomFields = user?.has_required_custom_fields ?? false;
  const customFieldsData: ProfileCustomFieldValue[] = customFields ?? [];

  // Only fetch employee profile for client 623217 when custom fields check is needed
  const isStudentAgentClient = user?.client_id === '623217';
  const needsJobCodeCheck =
    hasRequiredCustomFields &&
    customFieldsData.length > 0 &&
    isStudentAgentClient;

  const fetchEmployeeProfile = useCallback(async () => {
    try {
      const response = await api.profiles.getEmployeeProfile();
      setEmployeeProfile(response.results);
    } catch (error) {
      console.error('Error fetching employee profile:', error);
      setEmployeeProfile(null);
    }
  }, [api]);

  useEffect(() => {
    if (needsJobCodeCheck) {
      fetchEmployeeProfile();
    }
  }, [needsJobCodeCheck, fetchEmployeeProfile]);

  // Determine if user is eligible to see the custom fields modal
  const shouldShowCustomFieldsModal = useMemo(() => {
    if (!hasRequiredCustomFields || customFieldsData.length === 0) {
      return false;
    }

    // For the student agent client, require job code to include "student agent"
    if (isStudentAgentClient) {
      // Wait for employee profile to load before showing modal
      if (!employeeProfile) {
        return false;
      }
      const jobCodeDescription =
        employeeProfile.position?.employee_title?.toLowerCase() ?? '';
      return jobCodeDescription.includes('student agent');
    }

    return true;
  }, [
    hasRequiredCustomFields,
    customFieldsData.length,
    isStudentAgentClient,
    employeeProfile,
  ]);

  useEffect(() => {
    if (shouldShowCustomFieldsModal) {
      setCustomFieldsModalOpen(true);
    }
  }, [shouldShowCustomFieldsModal]);

  return (
    <>
      {!downLG && <HeaderSearch />}
      {downLG && <Box sx={{ width: '100%', ml: 1 }} />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          ml: 'auto',
          gap: 1.5,
        }}
      >
        <ClientDropdown />
        <OpenEnrollmentNotification />
        <AppNotification />
        {!downLG && <Profile />}
        {downLG && <MobileSection />}
      </Box>
      <HandbookAssignmentPrompt />
      {csaAcknowledgementEnabled && <CsaAcknowledgementPrompt />}

      {customFieldsModalOpen && (
        <UpdateUserDefinedFieldsModal
          open={customFieldsModalOpen}
          onClose={() => setCustomFieldsModalOpen(false)}
          refreshProfile={refreshCustomFields}
          customFields={customFieldsData}
          isRequired={true}
        />
      )}
    </>
  );
};

export default HeaderContent;
